/*
 * Dead-letter inspector — every job that exhausted retries lives here
 * with its error text, last input, and a Requeue / Bulk-requeue action.
 * Reuses existing api.jobs / api.requeueJob endpoints; this page is
 * the focused triage surface that was previously buried under
 * AdminJobs's overall job table.
 */

import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../layouts/PageBreadcrumbs.js";
import { api, ApiError } from "../lib/api.js";
import { SkeletonTable } from "../components/Skeleton.js";

type Job = {
  id: string;
  type: string;
  status: string;
  workspaceId?: string;
  requestedAt: string;
  completedAt?: string;
  error?: string;
  input?: unknown;
};

export default function DeadLetterInspector() {
  const [jobs, setJobs] = useState<Job[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);

  const refresh = () => {
    api.jobs()
      .then((r) => setJobs((r.jobs as unknown as Job[]).filter((j) => j.status === "dead-lettered")))
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  };
  useEffect(refresh, []);

  const filtered = useMemo(() => {
    if (!jobs) return [];
    if (!search.trim()) return jobs;
    const q = search.toLowerCase();
    return jobs.filter((j) => [j.id, j.type, j.workspaceId, j.error].filter(Boolean).some((f) => String(f).toLowerCase().includes(q)));
  }, [jobs, search]);

  async function requeue(id: string) {
    setBusyId(id);
    try {
      await api.requeueJob(id);
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  }

  async function requeueAll() {
    if (!filtered.length) return;
    if (!window.confirm(`Requeue ${filtered.length} dead-lettered job(s)?`)) return;
    setBulkBusy(true);
    try {
      await api.bulkRequeueDeadLettered({ jobIds: filtered.map((j) => j.id) });
      refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBulkBusy(false);
    }
  }

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Dead-letter inspector</h1>
          <p className="muted">
            Jobs that exhausted retries. Inspect the error, fix the underlying cause, then requeue —
            singly or in bulk.
          </p>
        </div>
        <div className="actions">
          <button type="button" className="btn btn-secondary" onClick={refresh}>Refresh</button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => void requeueAll()}
            disabled={bulkBusy || !filtered.length}
          >
            {bulkBusy ? "Requeuing…" : `Requeue ${filtered.length}`}
          </button>
        </div>
      </header>

      {error && <div className="ts-alert ts-alert-error">{error}</div>}

      <section className="transfer-filters">
        <label className="transfer-filter transfer-filter-grow">
          Search
          <input type="search" placeholder="id / type / workspace / error…" value={search} onChange={(e) => setSearch(e.target.value)} />
        </label>
      </section>

      {jobs === null && <SkeletonTable rows={4} columns={6} />}
      {jobs !== null && filtered.length === 0 && (
        <div className="empty-state-card">
          <h2 style={{ marginTop: 0 }}>No dead-lettered jobs</h2>
          <p>
            Healthy state — every queued job has either completed or is still being retried. If you expected failures here,
            check Sync activity for jobs in the failed state that haven't yet exhausted their attempts.
          </p>
        </div>
      )}

      {filtered.length > 0 && (
        <table className="transfer-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Type</th>
              <th>Workspace</th>
              <th>Failed at</th>
              <th>Error</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((j) => (
              <tr key={j.id}>
                <td className="mono" style={{ fontSize: 11 }}>{j.id}</td>
                <td><span className="pill pill-muted">{j.type}</span></td>
                <td className="mono" style={{ fontSize: 11 }}>{j.workspaceId ?? "—"}</td>
                <td className="muted" style={{ fontSize: 12 }}>{j.completedAt ?? "—"}</td>
                <td className="error-text" style={{ fontSize: 12, maxWidth: 480 }}>{j.error ?? "—"}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-ghost btn-sm"
                    disabled={busyId === j.id}
                    onClick={() => void requeue(j.id)}
                  >
                    {busyId === j.id ? "Requeuing…" : "Requeue"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
